import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * Log user activity to the backend
 * @param {string} actionType - Type of action (page_view, button_click, navigation, etc.)
 * @param {string} actionDetail - Specific description of the action
 * @param {object} metadata - Additional context data
 */
export const logActivity = async (actionType, actionDetail = null, metadata = null) => {
  const token = localStorage.getItem('session_token');
  if (!token) return; // Don't log for unauthenticated users
  
  try {
    await axios.post(
      `${API}/activity/log`,
      {
        action_type: actionType,
        action_detail: actionDetail,
        page_path: window.location.pathname,
        metadata: metadata
      },
      {
        headers: { Authorization: `Bearer ${token}` }
      }
    );
  } catch (error) {
    // Silently fail - don't disrupt user experience for logging failures
    console.debug('Activity log failed:', error.message);
  }
};

/**
 * Track page view
 * @param {string} pageName - Name of the page being viewed
 */
export const trackPageView = (pageName) => {
  logActivity('page_view', pageName);
};

/**
 * Track button click
 * @param {string} buttonName - Name/identifier of the button clicked
 * @param {object} context - Additional context about the click
 */
export const trackButtonClick = (buttonName, context = null) => {
  logActivity('button_click', buttonName, context);
};

/**
 * Track navigation event
 * @param {string} from - Previous path
 * @param {string} to - New path
 */
export const trackNavigation = (from, to) => {
  logActivity('navigation', `${from} → ${to}`, { from, to });
};

/**
 * Track form submission
 * @param {string} formName - Name of the form
 * @param {boolean} success - Whether submission was successful
 */
export const trackFormSubmit = (formName, success = true) => {
  logActivity('form_submit', formName, { success });
};

/**
 * Track feature usage
 * @param {string} featureName - Name of the feature used
 * @param {object} details - Feature usage details
 */
export const trackFeatureUse = (featureName, details = null) => {
  logActivity('feature_use', featureName, details);
};

/**
 * Track error occurrence
 * @param {string} errorMessage - Error message or description
 * @param {object} errorContext - Additional error context
 */
export const trackError = (errorMessage, errorContext = null) => {
  logActivity('error', errorMessage, errorContext);
};

export default {
  logActivity,
  trackPageView,
  trackButtonClick,
  trackNavigation,
  trackFormSubmit,
  trackFeatureUse,
  trackError
};
